import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getUserGemeenschappenByRole } from '../services/gemeenschappen';
import { updateUserProfile } from '../services/users';
import SectionHelpButton from '../components/SectionHelpButton';
import EmptyState from '../components/EmptyState';
import { trackEvent } from '../services/analytics';
import './UserDashboardPage.css';

/**
 * User Dashboard Page (Feature 12)
 *
 * Shows the signed-in user's profile (editable first name, last name and
 * phone) and the gemeenschappen assigned to them, split by role:
 * verantwoordelijke and catechist.
 */
const UserDashboardPage = () => {
  const { user, userProfile, refreshProfile } = useAuth();

  // Assigned gemeenschappen by role
  const [asVerantwoordelijke, setAsVerantwoordelijke] = useState([]);
  const [asCatechist, setAsCatechist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Profile editing state
  const [isEditing, setIsEditing] = useState(false);
  const [profileForm, setProfileForm] = useState({ voornaam: '', achternaam: '', telefoon: '' });
  const [profileErrors, setProfileErrors] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    let isCancelled = false;

    const loadAssignments = async () => {
      if (!user?.email) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const { asVerantwoordelijke: verantwoordelijk, asCatechist: catechist } =
          await getUserGemeenschappenByRole(user.email);
        if (!isCancelled) {
          setAsVerantwoordelijke(verantwoordelijk);
          setAsCatechist(catechist);
        }
      } catch (err) {
        console.error('Error loading dashboard gemeenschappen:', err);
        if (!isCancelled) setError('Fout bij het laden van uw gemeenschappen');
      } finally {
        if (!isCancelled) setLoading(false);
      }
    };

    loadAssignments();

    return () => {
      isCancelled = true;
    };
  }, [user?.email]);

  const displayName =
    `${userProfile?.voornaam || ''} ${userProfile?.achternaam || ''}`.trim() ||
    user?.email ||
    'Gebruiker';
  const userEmail = user?.email || '';

  const startEditing = () => {
    setProfileForm({
      voornaam: userProfile?.voornaam || '',
      achternaam: userProfile?.achternaam || '',
      telefoon: userProfile?.telefoon || ''
    });
    setProfileErrors({});
    setSuccessMessage('');
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setProfileErrors({});
    setIsEditing(false);
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileForm((prev) => ({ ...prev, [name]: value }));
  };

  const validateProfile = () => {
    const errors = {};
    if (!profileForm.voornaam.trim()) errors.voornaam = 'Voornaam is verplicht';
    if (!profileForm.achternaam.trim()) errors.achternaam = 'Achternaam is verplicht';
    setProfileErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!validateProfile()) return;
    setSavingProfile(true);
    setSuccessMessage('');
    try {
      await updateUserProfile(user.uid, {
        voornaam: profileForm.voornaam,
        achternaam: profileForm.achternaam,
        telefoon: profileForm.telefoon
      });
      await refreshProfile();
      trackEvent('profile_updated');
      setSuccessMessage('Uw gegevens zijn succesvol bijgewerkt.');
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating profile:', err);
      setProfileErrors({ form: 'Fout bij het opslaan van uw gegevens. Probeer het opnieuw.' });
    } finally {
      setSavingProfile(false);
    }
  };

  const renderGemeenschapTable = (gemeenschappen) => (
    <div className="dashboard-table-container">
      <table className="dashboard-table">
        <thead>
          <tr>
            <th>Bisdom</th>
            <th>Parochie</th>
            <th>Gemeenschap</th>
            <th>Aantal leden</th>
            <th>Etappe</th>
          </tr>
        </thead>
        <tbody>
          {gemeenschappen.map((item) => (
            <tr key={item.id}>
              <td data-label="Bisdom">{item.bisdom || '-'}</td>
              <td data-label="Parochie">{item.parochie || '-'}</td>
              <td data-label="Gemeenschap">{item.gemeenschap || '-'}</td>
              <td data-label="Aantal leden">{item.aantalLeden ?? '-'}</td>
              <td data-label="Etappe" className="stacked-cell">
                <div className="cell-primary">{item.laatsteEtappe || '-'}</div>
                {item.laatsteEtappe && (
                  <div className="cell-secondary status-inline">
                    {item.laatsteEtappeAfgesloten ? (
                      <span className="status-badge completed">✓ Afgesloten</span>
                    ) : (
                      <span className="status-badge pending">- Niet afgesloten</span>
                    )}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  if (loading) {
    return (
      <div className="user-dashboard-page">
        <div className="loading">Laden...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-dashboard-page">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="user-dashboard-page">
      <div className="app-page-header">
        <div className="app-page-heading">
          <div className="app-page-title-row">
            <h2 className="app-page-title">Mijn overzicht</h2>
            <SectionHelpButton
              title="Mijn overzicht"
              purpose="Dit overzicht toont uw persoonlijke gegevens en de gemeenschappen waarvoor u als verantwoordelijke of catechist geregistreerd bent."
              instructions={[
                'U kunt uw voornaam, achternaam en telefoonnummer aanpassen via de knop "Gegevens bewerken".',
                'De lijsten tonen alleen gemeenschappen waarvoor u als verantwoordelijke of catechist bent toegewezen.'
              ]}
            />
          </div>
          <p className="app-page-description">
            Uw persoonlijke gegevens en toegewezen gemeenschappen op één plek.
          </p>
        </div>
      </div>

      <section className="dashboard-card profile-card">
        <div className="dashboard-card-header">
          <h3>Mijn gegevens</h3>
          {!isEditing && (
            <button type="button" className="dashboard-edit-btn" onClick={startEditing}>
              Gegevens bewerken
            </button>
          )}
        </div>

        {isEditing ? (
          <form className="profile-form" onSubmit={handleProfileSubmit} noValidate>
            <div className="profile-form-grid">
              <div className="profile-field">
                <label htmlFor="profile-voornaam">Voornaam</label>
                <input
                  id="profile-voornaam"
                  name="voornaam"
                  value={profileForm.voornaam}
                  onChange={handleProfileChange}
                  className={profileErrors.voornaam ? 'input-error' : ''}
                />
                {profileErrors.voornaam && (
                  <span className="field-error" role="alert">
                    {profileErrors.voornaam}
                  </span>
                )}
              </div>
              <div className="profile-field">
                <label htmlFor="profile-achternaam">Achternaam</label>
                <input
                  id="profile-achternaam"
                  name="achternaam"
                  value={profileForm.achternaam}
                  onChange={handleProfileChange}
                  className={profileErrors.achternaam ? 'input-error' : ''}
                />
                {profileErrors.achternaam && (
                  <span className="field-error" role="alert">
                    {profileErrors.achternaam}
                  </span>
                )}
              </div>
              <div className="profile-field">
                <label htmlFor="profile-telefoon">Telefoon</label>
                <input
                  id="profile-telefoon"
                  name="telefoon"
                  type="tel"
                  value={profileForm.telefoon}
                  onChange={handleProfileChange}
                />
              </div>
            </div>

            {profileErrors.form && (
              <div className="validation-message" role="alert">
                {profileErrors.form}
              </div>
            )}

            <div className="profile-form-actions">
              <button type="button" className="dashboard-cancel-btn" onClick={cancelEditing}>
                Annuleren
              </button>
              <button type="submit" className="dashboard-save-btn" disabled={savingProfile}>
                {savingProfile ? 'Opslaan...' : 'Opslaan'}
              </button>
            </div>
          </form>
        ) : (
          <dl className="profile-details">
            <div className="profile-detail-row">
              <dt>Naam</dt>
              <dd>{displayName}</dd>
            </div>
            <div className="profile-detail-row">
              <dt>Email</dt>
              <dd>{userEmail}</dd>
            </div>
            <div className="profile-detail-row">
              <dt>Telefoon</dt>
              <dd>{userProfile?.telefoon || '-'}</dd>
            </div>
          </dl>
        )}

        {successMessage && (
          <div className="feedback-success" role="status">
            {successMessage}
          </div>
        )}
      </section>

      <section className="dashboard-card">
        <div className="dashboard-card-header">
          <h3>Mijn Gemeenschappen als Verantwoordelijke</h3>
          {asVerantwoordelijke.length > 0 && (
            <span className="dashboard-count">{asVerantwoordelijke.length}</span>
          )}
        </div>
        {asVerantwoordelijke.length === 0 ? (
          <EmptyState
            icon="🏠"
            message="U bent niet geregistreerd als verantwoordelijke voor een gemeenschap."
            hint="Maak een nieuwe gemeenschap aan, of neem contact op met de administrator om als verantwoordelijke te worden toegewezen."
          />
        ) : (
          renderGemeenschapTable(asVerantwoordelijke)
        )}
      </section>

      <section className="dashboard-card">
        <div className="dashboard-card-header">
          <h3>Mijn Gemeenschappen als Catechist</h3>
          {asCatechist.length > 0 && (
            <span className="dashboard-count">{asCatechist.length}</span>
          )}
        </div>
        {asCatechist.length === 0 ? (
          <EmptyState
            icon="📖"
            message="U bent niet geregistreerd als catechist voor een gemeenschap."
            hint="Maak een nieuwe gemeenschap aan, of neem contact op met de administrator om als catechist te worden toegewezen."
          />
        ) : (
          renderGemeenschapTable(asCatechist)
        )}
      </section>
    </div>
  );
};

export default UserDashboardPage;